/**
 * Login error codes used when redirecting to /login?error=N or /customers-login?error=N.
 * Each code has a user-facing message so the user understands what went wrong.
 * Log the reason (e.g. cookie_unavailable, permission_context) before redirecting for support.
 */
export const LOGIN_ERROR_CODES = {
	GENERIC: "1",
	TOKEN_REFRESH_FAILED: "2",
	EMAIL_NOT_FOUND: "3",
	CONFLICT_409: "4",
	RATE_LIMIT: "5",
	COOKIE_UNAVAILABLE: "6",
	PERMISSION_CONTEXT_AFTER_AUTH: "7",
	NO_REFRESH_TOKEN: "8",
	PERMISSION_CONTEXT_AFTER_REFRESH: "9",
	CUSTOMER_LOGIN_CALLBACK_FAILED: "10",
	CUSTOMER_COOKIE_UNAVAILABLE: "11",
	NO_SESSION: "12",
	/** Compte non configuré (ex. rôle ou étude manquants). */
	ACCOUNT_NOT_CONFIGURED: "13",
	/** Aucune étude associée ou configuration d'étude incomplète. */
	NO_OFFICE_CONFIGURED: "14",
	/** Échec d’échange du code d’autorisation ID.not → session (réponse 401 « Invalid token » côté API LeCoffre). */
	IDNOT_CODE_EXCHANGE_FAILED: "15",
	/** API injoignable ou passerelle en erreur (502 / 503 / 504) — cause côté infrastructure, pas un refus ID.not. */
	API_UNAVAILABLE_OR_GATEWAY: "16",
} as const;

export type LoginErrorCodeKey = keyof typeof LOGIN_ERROR_CODES;

/**
 * User-facing messages: what happened + precise reason (vulgarised, factual, no metaphor).
 * So the user understands both that the connection failed and why.
 */
export const LOGIN_ERROR_MESSAGES: Record<string, string> = {
	[LOGIN_ERROR_CODES.GENERIC]:
		"La connexion n'a pas abouti. Le serveur d'authentification a renvoyé une erreur non précisée. Réessayez ou contactez le support.",
	[LOGIN_ERROR_CODES.TOKEN_REFRESH_FAILED]:
		"La connexion n'a pas abouti. Le renouvellement de votre session a échoué (token expiré ou invalide). Reconnectez-vous.",
	[LOGIN_ERROR_CODES.EMAIL_NOT_FOUND]:
		"La connexion n'a pas abouti. Cet email n'est pas reconnu par le serveur d'authentification. Vérifiez l'adresse ou contactez l'administrateur.",
	[LOGIN_ERROR_CODES.CONFLICT_409]:
		"La connexion n'a pas abouti. Le serveur signale un conflit sur le compte (ex. double utilisation). Contactez le support.",
	[LOGIN_ERROR_CODES.RATE_LIMIT]:
		"La connexion n'a pas abouti. Trop de tentatives en peu de temps ; le serveur a temporairement bloqué les demandes. Réessayez dans quelques minutes.",
	[LOGIN_ERROR_CODES.COOKIE_UNAVAILABLE]:
		"La connexion n'a pas abouti. La session n'a pas pu être enregistrée dans le navigateur (cookie absent, bloqué ou refusé). Autorisez les cookies pour ce site ou réessayez.",
	[LOGIN_ERROR_CODES.PERMISSION_CONTEXT_AFTER_AUTH]:
		"La connexion n'a pas abouti. Après identification, l'application n'a pas pu récupérer vos droits d'accès (réponse vide ou erreur du serveur). Réessayez ou contactez le support.",
	[LOGIN_ERROR_CODES.NO_REFRESH_TOKEN]:
		"La connexion n'a pas abouti. Aucun jeton de session n'est présent (session expirée ou jamais enregistrée). Reconnectez-vous.",
	[LOGIN_ERROR_CODES.PERMISSION_CONTEXT_AFTER_REFRESH]:
		"La connexion n'a pas abouti. Après actualisation de la session, vos droits d'accès n'ont pas pu être récupérés. Reconnectez-vous ou contactez le support.",
	[LOGIN_ERROR_CODES.CUSTOMER_LOGIN_CALLBACK_FAILED]:
		"La connexion espace client n'a pas abouti. L'échange avec le serveur après identification a échoué. Réessayez ou contactez l'administrateur.",
	[LOGIN_ERROR_CODES.CUSTOMER_COOKIE_UNAVAILABLE]:
		"La connexion espace client n'a pas abouti. La session client n'a pas été enregistrée dans le navigateur (cookie absent ou bloqué). Autorisez les cookies ou réessayez.",
	[LOGIN_ERROR_CODES.NO_SESSION]:
		"La connexion n'a pas abouti. Aucune session active. Retournez à la page de connexion et identifiez-vous avec ID.not.",
	[LOGIN_ERROR_CODES.ACCOUNT_NOT_CONFIGURED]:
		"La connexion n'a pas abouti. Votre compte n'est pas correctement configuré (droits ou étude manquants). Contactez l'administrateur de votre étude.",
	[LOGIN_ERROR_CODES.NO_OFFICE_CONFIGURED]:
		"La connexion n'a pas abouti. Aucune étude n'est associée à votre compte ou la configuration est incomplète. Contactez l'administrateur de votre étude.",
	[LOGIN_ERROR_CODES.IDNOT_CODE_EXCHANGE_FAILED]:
		"La connexion n'a pas abouti. L'échange avec ID.not a échoué (code expiré, session déjà utilisée, ou configuration OAuth incompatible avec cette adresse). Rechargez la page de connexion et réessayez ; si le problème continue, contactez le support.",
	[LOGIN_ERROR_CODES.API_UNAVAILABLE_OR_GATEWAY]:
		"La connexion n'a pas abouti. Le service d'application est momentanément indisponible ou en maintenance (réponse 502/503). Réessayez dans quelques minutes. Si le blocage dure, contactez le support en précisant l'heure et la page de connexion.",
};

export function getLoginErrorMessage(errorParam: string | string[] | undefined): string | null {
	if (errorParam == null) {
		return null;
	}
	const code = Array.isArray(errorParam) ? errorParam[0] : errorParam;
	if (typeof code !== "string" || code === "") {
		return null;
	}
	return LOGIN_ERROR_MESSAGES[code] ?? LOGIN_ERROR_MESSAGES[LOGIN_ERROR_CODES.GENERIC] ?? null;
}
